<script setup lang="ts">
import { computed, ref } from "vue";
import { useMounted } from "@vueuse/core";
import { Swiper, SwiperSlide } from "swiper/vue";
import {
  FreeMode,
  Thumbs,
  EffectFade,
  Pagination,
  Autoplay,
  Navigation,
} from "swiper/modules";
import type { Swiper as SwiperType } from "swiper/types";

(() => Swiper)();
(() => SwiperSlide)();

interface Slide {
  id: number;
  name: string;
  image: string;
  action_url: string;
  sm_image_url: string;
}

// Props
const props = withDefaults(
  defineProps<{
    slides?: Slide[];
    secondarySlides?: Slide[];
    loading?: boolean;
  }>(),
  {
    slides: () => [],
    secondarySlides: () => [],
  },
);

// State
const swiperInstance = ref<SwiperType | null>(null);
const imageErrors = ref<boolean[]>([]);
const runningAutoplay = ref(true);
const mounted = useMounted();

// Swiper config
const modules = [
  FreeMode,
  Thumbs,
  EffectFade,
  Pagination,
  Autoplay,
  Navigation,
];

const pagination = {
  clickable: true,
  renderBullet: (_index: number, className: string) =>
    '<span class="' +
    className +
    ' d-lg-none" style="transition: all 0.3s ease-out !important;"></span>',
};

// Computed
const widthBullet = computed(() => Math.floor(100 / (props.slides.length + 1)));
const widthActiveBullet = computed(
  () => 90 - widthBullet.value * (props.slides.length - 1),
);
const canLoopMainCarousel = computed(() => props.slides.length > 1);
const canLoopSecondaryCarousel = computed(
  () => props.secondarySlides.length > 3,
);
const firstSlide = computed(() => props.slides[0]);

const getFallbackImage = (slider: Slide) => slider.image || slider.sm_image_url;

const hasRenderableImage = (slider: Slide) =>
  Boolean(slider.image || slider.sm_image_url);

const onSwiper = (swiper: SwiperType) => {
  swiperInstance.value = swiper;

  const stopAutoplay = () => {
    runningAutoplay.value = false;
  };
  const startAutoplay = () => {
    runningAutoplay.value = true;
  };

  swiperInstance.value.on("autoplayStop", stopAutoplay);
  swiperInstance.value.on("autoplayStart", startAutoplay);
};

// Expose for template (implícito en <script setup>)
</script>

<template>
  <div
    v-if="slides.length > 0"
    class="position-relative mt-0 mt-md-5 mt-lg-0 carousell-images yes-carousel ms-lg-4"
  >
    <div
      v-if="!mounted"
      class="position-relative pt-0 pt-md-2 pt-lg-0 carousel-stage"
    >
      <nuxt-link :to="firstSlide?.action_url || '#'" class="d-block">
        <picture
          v-if="firstSlide && hasRenderableImage(firstSlide)"
          class="d-block ssr-single-carousel-image"
        >
          <source
            v-if="firstSlide.sm_image_url"
            media="(max-width: 991.98px)"
            :srcset="firstSlide.sm_image_url"
          />
          <img
            :src="getFallbackImage(firstSlide)"
            width="1200"
            height="450"
            :alt="firstSlide.name"
            class="ssr-single-carousel-image"
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
        </picture>
        <div
          v-else
          class="ssr-single-carousel-image no-carousel-skeleton"
          aria-hidden="true"
        />
      </nuxt-link>
    </div>
    <div v-else class="position-relative pt-0 pt-md-2 pt-lg-0 carousel-stage">
      <swiper
        :modules="modules"
        :style="{
          '--swiper-navigation-color': '#151515',
          '--swiper-pagination-color': '#555865',
          '--swiper-pagination-bullet-size': '10px',
          '--swiper-pagination-bullet-width': widthBullet + '%',
          '--swiper-pagination-bullet-active-width': widthActiveBullet + '%',
          '--swiper-pagination-bullet-height': '6px',
          '--swiper-pagination-bullet-inactive-color': '#555865',
          '--swiper-pagination-bullet-inactive-opacity': '0.2',
          '--swiper-pagination-bullet-opacity': '1',
          '--swiper-pagination-bullet-horizontal-gap': '2px',
          '--swiper-pagination-bullet-vertical-gap': '2px',
          '--swiper-pagination-bullet-border-radius': '0px',
        }"
        :lazy="true"
        :slides-per-view="1"
        :loop="canLoopMainCarousel"
        :space-between="0"
        :navigation="false"
        :pagination="pagination"
        :autoplay="
          canLoopMainCarousel
            ? {
                delay: 6500,
                disableOnInteraction: false,
              }
            : false
        "
        class="CustomSwiper"
        effect="fade"
        @swiper="onSwiper"
      >
        <!-- <swiper-slide v-if="loading">
          <div class="spinner-border text-primary py-5">
            <span class="visually-hidden">Loading...</span>
          </div>
        </swiper-slide> -->
        <swiper-slide
          v-for="(slide, index) in slides"
          :key="index"
          style="width: 100%"
        >
          <nuxt-link :to="slide.action_url || '#'">
            <picture v-if="hasRenderableImage(slide) && !imageErrors[slide.id]">
              <source
                v-if="slide.sm_image_url"
                media="(max-width: 991.98px)"
                :srcset="slide.sm_image_url"
              />
              <img
                :src="getFallbackImage(slide)"
                width="1200"
                height="450"
                :alt="slide.name"
                :loading="index === 0 ? 'eager' : 'lazy'"
                :fetchpriority="index === 0 ? 'high' : 'auto'"
                decoding="async"
                @error="imageErrors[slide.id] = true"
              />
            </picture>
            <div
              v-if="imageErrors[slide.id] || !hasRenderableImage(slide)"
              class="d-flex justify-content-center align-items-center"
            >
              <span
                class="text-secondary fw-semibold text-capitalize fs-8 px-2 text-center"
              >
                {{ slide.name }}
              </span>
            </div>
          </nuxt-link>
        </swiper-slide>
      </swiper>
      <div
        v-if="slides.length > 1 && swiperInstance"
        class="position-absolute top-0 end-0 d-none d-lg-block"
        :style="{ zIndex: 1 }"
      >
        <button
          v-if="runningAutoplay"
          type="button"
          class="btn btn-link m-4 p-2 rounded-pill text-primary"
          :style="{ backgroundColor: 'rgba(179, 179, 179, .5)' }"
          @click="swiperInstance.autoplay.stop()"
        >
          <i class="bi bi-pause-fill fs-1" />
        </button>
        <button
          v-else
          type="button"
          class="btn btn-link m-4 p-2 rounded-pill text-primary"
          :style="{ backgroundColor: 'rgba(179, 179, 179, .5)' }"
          @click="swiperInstance.autoplay.start()"
        >
          <i class="bi bi-play-fill fs-1" />
        </button>
      </div>
      <div
        v-if="slides.length > 1 && swiperInstance"
        class="position-absolute top-50 end-0 translate-middle-y d-none d-lg-block"
        :style="{ zIndex: 1 }"
        @click="swiperInstance.slideNext()"
      >
        <div class="swiper-button-next-carousel m-3 text-primary">
          <i
            class="bi bi-chevron-compact-right"
            :style="{ fontSize: '50px !important' }"
          />
        </div>
      </div>
      <div
        v-if="slides.length > 1 && swiperInstance"
        class="position-absolute top-50 start-0 translate-middle-y pointer d-none d-lg-block"
        :style="{ zIndex: 1 }"
        @click="swiperInstance.slidePrev()"
      >
        <div class="swiper-button-prev-carousel m-3 text-primary">
          <i
            class="bi bi-chevron-compact-left"
            :style="{ fontSize: '50px !important' }"
          />
        </div>
      </div>
    </div>
    <div
      v-if="!mounted && secondarySlides.length > 0"
      class="CustomSwiperThumb d-none d-lg-flex align-items-stretch secondary-carousel-fallback"
    >
      <nuxt-link
        v-for="(slide, index) in secondarySlides.slice(0, 3)"
        :key="index"
        :to="slide.action_url || '#'"
        class="secondary-carousel-fallback__item px-3 bg-transparent"
      >
        <img
          v-if="hasRenderableImage(slide) && !imageErrors[slide.id]"
          :src="slide.image || slide.sm_image_url"
          width="370"
          height="150"
          :alt="slide.name"
          class="small-slider-img"
          loading="lazy"
          decoding="async"
          @error="imageErrors[slide.id] = true"
        />
        <div
          v-else
          class="bg-body-secondary d-flex justify-content-center align-items-center w-100 CustomSwiperThumbImage"
        >
          <span
            class="text-secondary fw-semibold text-capitalize fs-8 px-2 text-center"
          >
            {{ slide.name }}
          </span>
        </div>
      </nuxt-link>
    </div>
    <swiper
      v-else-if="secondarySlides.length > 0"
      :modules="modules"
      :watch-slides-progress="true"
      :loop="canLoopSecondaryCarousel"
      :space-between="0"
      :slides-per-view="3"
      :free-mode="true"
      class="CustomSwiperThumb d-none d-lg-block"
    >
      <swiper-slide
        v-for="(slide, index) in secondarySlides"
        :key="index"
        class="opacity-100 px-3 bg-transparent"
        :style="{ width: 100 / secondarySlides.length + '%' }"
      >
        <nuxt-link :to="slide.action_url || '#'" class="w-100 bg-white">
          <img
            v-if="hasRenderableImage(slide) && !imageErrors[slide.id]"
            :src="slide.image || slide.sm_image_url"
            width="370"
            height="150"
            :alt="slide.name"
            class="small-slider-img"
            loading="lazy"
            decoding="async"
            @error="imageErrors[slide.id] = true"
          />
          <div
            v-if="imageErrors[slide.id] || !hasRenderableImage(slide)"
            class="bg-body-secondary d-flex justify-content-center align-items-center w-100 CustomSwiperThumbImage"
          >
            <span
              class="text-secondary fw-semibold text-capitalize fs-8 px-2 text-center"
            >
              {{ slide.name }}
            </span>
          </div>
        </nuxt-link>
      </swiper-slide>
    </swiper>
  </div>
  <div v-else class="no-carousel ms-lg-3" aria-hidden="true">
    <div class="no-carousel-skeleton"></div>
  </div>
</template>

<style lang="sass" scoped>
.swiper
    width: 100%
    height: 100%

.swiper-slide
    text-align: center
    font-size: 18px
    background: #fff
    display: flex
    justify-content: center
    align-items: center

.CustomSwiper .swiper-slide
    display: block
    margin: 0 auto
    width: 390px
    height: auto
    min-height: 216px
    max-height: 420px

    @media (min-width: 768px)
        min-height: 450px
        max-height: 580px

.CustomSwiper,
.CustomSwiper .swiper-slide img
    width:100%
    height: 100%

.carousel-stage
    overflow: hidden
    border-radius: 12px

.small-slider-img
    display: block
    width: 100%
    height: 100%
    object-fit: cover

.secondary-carousel-fallback__item
    flex: 1 1 0
    min-width: 0


.CustomSwiperThumb
    box-sizing: border-box
    padding: 0 10px
    position: absolute
    left: .5%
    width: 99%
    background-color: transparent
    height: 100px
    bottom: -35px

    @media (min-width: 768px)
        bottom: -115px
        height: 170px

    @media (min-width: 992px)
        bottom: -120px

    @media (min-width: 1200px)
        bottom: -105px

    @media (min-width: 1400px)
        bottom: -105px

    @media (min-width: 1700px)
        bottom: -40px

    @media (min-width: 1800px)
        bottom: -70px

.CustomSwiperThumb .swiper-slide
    width: 25%
    height: 100%
    opacity: 0.7

.CustomSwiperThumb .swiper-slide-thumb-active
    opacity: 1

.CustomSwiperThumbImage
    display: block
    width: 100%
    object-fit: fill
    height: 80px
    @media (min-width: 992px)
        height: 150px

.swiper-pagination-bullet-active
    width: var(--swiper-pagination-bullet-active-width, var(--swiper-pagination-bullet-size, 8px)) !important

.swiper-button-next-carousel, .carousel .swiper-button-prev-carousel
    background-color: transparent
    padding: 30px 5px !important
    border-radius: 15px !important
    cursor: pointer

.no-carousel
  width: 100%
  min-height: 170px

  @media (min-width: 768px)
    min-height: 450px

.ssr-single-carousel-image
  display: block
  width: 100%
  min-height: 170px
  height: 100%
  object-fit: cover

  @media (min-width: 768px)
    min-height: 450px

.no-carousel-skeleton
  width: 100%
  height: 100%
  min-height: inherit
  border-radius: 12px
  background: linear-gradient(90deg, #f2f4f7 0%, #e9edf2 50%, #f2f4f7 100%)
  background-size: 200% 100%
  animation: carouselSkeletonShimmer 1.4s ease-in-out infinite

@keyframes carouselSkeletonShimmer
  0%
    background-position: 200% 0
  100%
    background-position: -200% 0
</style>
